import {
  useLocation,
  useNavigate,
  useParams,
  useSearchParams,
} from 'react-router-dom'

type Query = Record<string, string | string[] | undefined>

type NavigateFn = (to: string | number, opts?: { replace?: boolean }) => void

let externalNavigate: NavigateFn | null = null

export function setRouterNavigate(fn: NavigateFn) {
  externalNavigate = fn
}

function mergeQuery(
  params: Record<string, string | undefined>,
  search: string,
): Query {
  const query: Query = {}
  for (const [key, value] of Object.entries(params)) {
    if (value !== undefined) query[key] = value
  }
  new URLSearchParams(search).forEach((value, key) => {
    const existing = query[key]
    if (existing === undefined) query[key] = value
    else if (Array.isArray(existing)) existing.push(value)
    else query[key] = [existing, value]
  })
  return query
}

/** Path params for imperative Router.query (no React tree). */
function pathParamsFromPathname(pathname: string): Record<string, string | undefined> {
  const parts = pathname.split('/').filter(Boolean)
  const params: Record<string, string | undefined> = {}
  if (parts[0]) params.courseId = parts[0]
  // /:courseId/:tabId (numeric tab) or named segments
  if (parts[1] && parts[1] !== 'folder' && parts[1] !== 'freecontent' &&
      parts[1] !== 'physicaladdress' && parts[1] !== 'videopreview' &&
      parts[1] !== 'teststats' && parts[1] !== 'checkout') {
    params.tabId = parts[1]
  }
  if (parts[1] === 'folder' && parts[2]) params.folderId = parts[2]
  if (parts[1] === 'teststats') {
    if (parts[2]) params.testId = parts[2]
    if (parts[3]) params.contentId = parts[3]
  }
  return params
}

function windowQuery(): Query {
  if (typeof window === 'undefined') return {}
  return mergeQuery(pathParamsFromPathname(window.location.pathname), window.location.search)
}

function parseUrl(url: string | { pathname?: string; query?: Query }): string {
  if (typeof url === 'string') return url
  const path = url.pathname || '/'
  if (!url.query) return path
  const sp = new URLSearchParams()
  for (const [k, v] of Object.entries(url.query)) {
    if (v === undefined) continue
    if (Array.isArray(v)) v.forEach((item) => sp.append(k, String(item)))
    else sp.set(k, String(v))
  }
  const qs = sp.toString()
  return qs ? `${path}?${qs}` : path
}

function useNextRouter() {
  const navigate = useNavigate()
  const location = useLocation()
  const params = useParams()
  const [searchParams] = useSearchParams()

  const query = mergeQuery(
    params as Record<string, string | undefined>,
    searchParams.toString() ? `?${searchParams.toString()}` : location.search,
  )

  // Also merge string form of location.search to be safe
  const query2 = mergeQuery(
    params as Record<string, string | undefined>,
    location.search,
  )

  const asPath = `${location.pathname}${location.search}${location.hash}`

  return {
    pathname: location.pathname,
    asPath,
    query: query2,
    route: location.pathname,
    isReady: true,
    push: (url: string | { pathname?: string; query?: Query }) => {
      navigate(parseUrl(url))
      return Promise.resolve(true)
    },
    replace: (url: string | { pathname?: string; query?: Query }) => {
      navigate(parseUrl(url), { replace: true })
      return Promise.resolve(true)
    },
    back: () => navigate(-1),
    reload: () => window.location.reload(),
    prefetch: async () => {},
    events: { on: () => {}, off: () => {}, emit: () => {} },
  }
}

export function useRouter() {
  return useNextRouter()
}

const Router: any = {
  push: (url: string | { pathname?: string; query?: Query }) => {
    const href = parseUrl(url)
    if (externalNavigate) externalNavigate(href)
    else {
      window.history.pushState({}, '', href)
      window.dispatchEvent(new PopStateEvent('popstate'))
    }
    return Promise.resolve(true)
  },
  replace: (url: string | { pathname?: string; query?: Query }) => {
    const href = parseUrl(url)
    if (externalNavigate) externalNavigate(href, { replace: true })
    else {
      window.history.replaceState({}, '', href)
      window.dispatchEvent(new PopStateEvent('popstate'))
    }
    return Promise.resolve(true)
  },
  back: () => {
    if (externalNavigate) externalNavigate(-1)
    else window.history.back()
  },
  get pathname() {
    return typeof window !== 'undefined' ? window.location.pathname : '/'
  },
  get asPath() {
    return typeof window !== 'undefined'
      ? `${window.location.pathname}${window.location.search}${window.location.hash}`
      : '/'
  },
  get route() {
    return typeof window !== 'undefined' ? window.location.pathname : '/'
  },
  /** Live query from current URL (legacy code reads Router.query / Router.router.query) */
  get query() {
    return windowQuery()
  },
  /** Nested singleton shape used in goBack etc. */
  get router() {
    return Router
  },
  events: { on: () => {}, off: () => {}, emit: () => {} },
  prefetch: async () => {},
}

export default Router
